import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, addDoc, query, orderBy, onSnapshot, updateDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { Box, TextField, Button, Typography, Paper, CircularProgress, Alert, Avatar, Tabs, Tab, useMediaQuery, useTheme } from '@mui/material';
import { Send as SendIcon, Pets as PetIcon, ArrowBack as ArrowBackIcon, Chat as ChatIcon, Psychology as TrainingIcon, Memory as MemoryIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { sendMessageToThread, categorizeConversation } from '../services/openai';
import RoadmapModule from './RoadmapModule';
import FlexibleRoadmap from './FlexibleRoadmap';
import PlanQaTab from './PlanQaTab';
import StickerStats from './StickerStats';
import RecentConversationMemory from './RecentConversationMemory';
import { ConversationTab, PlanQaContext } from '../types/planQa';
// Removed template imports - using only dynamic roadmap generation
import { analyzeConversationForRoadmap, generateRoadmapPrompt, parseAIResponseToRoadmap, generateConversationalRoadmapApproach, generateCompletionReflectionPrompt, generateFollowUpPlanSuggestionPrompt } from '../services/dynamicRoadmapGenerator';
import { DynamicRoadmapConfig } from '../services/dynamicRoadmapGenerator';
import { recentConversationMemoryService } from '../services/recentConversationMemoryService';
import { ConversationMemoryRequest } from '../types/recentMemory';
import { roadmapContextService } from '../services/roadmapContextService';
import { semanticIntentClassificationService } from '../services/semanticIntentClassificationService';
import { analyticsService } from '../services/analyticsService';
// Import custom hooks
import { useChatMessages, Message } from '../hooks/useChatMessages';
import { useThreadManagement } from '../hooks/useThreadManagement';
import { usePetContext } from '../hooks/usePetContext';
import { useSemanticIntent } from '../hooks/useSemanticIntent';
import { useRoadmapGeneration } from '../hooks/useRoadmapGeneration';
import ChatInputBar from './ChatInputBar';
import ChatLoadingIndicator from './ChatLoadingIndicator';
import ChatTabBar from './ChatTabBar';
import ChatMessageItem from './ChatMessageItem';

import ChatMessageList from './ChatMessageList';
import { scrollToBottom, attemptAutoScroll, isNearBottom } from '../utils/scrollUtils';
import { getLoadingProgressText, getRetryMessage, detectLanguageFromMessages, checkUserAgreement, checkUserDecline, getUIText } from '../utils/languageUtils';
import { getRoadmapFallbackText, translateRoadmap, extractPlanContextFromMessages, getRoadmapSuggestionText, generateConversationalStepPrompt } from '../utils/chatUtils';
import { cleanAIResponse } from '../utils/messageUtils';

// Temporary logging to check environment variables
console.log('Environment variables check:', {
  hasOpenAIKey: !!process.env.REACT_APP_OPENAI_API_KEY,
  hasAssistantId: !!process.env.REACT_APP_ASSISTANT_ID
});

interface ChatProps {
  conversationId: string;
  showRoadmapView: boolean;
  currentRoadmap: any;
  onRoadmapViewToggle: (roadmap: any) => void;
  onReturnToChat?: () => void; // New prop for direct return to chat
  onNavigateToHome?: () => void; // New prop for navigation to home
  onSwitchToPlanQa?: (context: PlanQaContext) => void; // New prop for Plan Q&A integration
  user: any; // User object with updated photoURL from Firestore
}

const Chat: React.FC<ChatProps> = ({
  conversationId,
  showRoadmapView,
  currentRoadmap,
  onRoadmapViewToggle,
  onReturnToChat,
  onNavigateToHome,
  onSwitchToPlanQa,
  user
}) => {
  console.log('Chat component rendered with conversationId:', conversationId);

  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Normalize language for memory component
  const memoryLanguage = (
    i18n.language.startsWith('zh') ? 'zh-CN' :
      i18n.language.startsWith('es') ? 'es' :
        i18n.language.startsWith('fr') ? 'fr' :
          'en'
  ) as 'zh-CN' | 'es' | 'fr' | 'en';

  // Use custom hook for message management
  const {
    messages,
    isLoading: isLoadingMessages,
    isTyping,
    setIsTyping,
    addMessage,
    updateMessage: updateMessageInDb,
    updateConversationTitle
  } = useChatMessages(
    conversationId,
    user?.uid,
    (error) => setError(t('errorLoadingMessages') + ': ' + error)
  );

  const [newMessage, setNewMessage] = useState('');
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use custom hook for thread management
  const {
    threadId,
    isInitializingThread,
    isThreadReady,
    initError,
    sendToThread
  } = useThreadManagement(
    conversationId,
    (error) => setError(t('errorInitializing') + ': ' + error)
  );

  // Handle thread initialization errors
  useEffect(() => {
    if (initError) {
      setError(initError);
    }
  }, [initError]);

  // Use custom hook for roadmap generation
  const {
    isGeneratingRoadmap,
    generateDynamicRoadmap,
    handleRoadmapTrigger
  } = useRoadmapGeneration({
    conversationId,
    userId: user?.uid,
    threadId,
    messages,
    setNewMessage,
    onRoadmapViewToggle
  });

  // Conversational roadmap flow state
  // ... rest of state declarations ...
  const [isInConversationalFlow, setIsInConversationalFlow] = useState(false);
  const [conversationalStep, setConversationalStep] = useState(0);
  const [conversationalQuestion, setConversationalQuestion] = useState('');
  const [conversationalContext, setConversationalContext] = useState('');

  // Completion reflection flow state
  const [isInCompletionReflection, setIsInCompletionReflection] = useState(false);
  const [completedRoadmap, setCompletedRoadmap] = useState<DynamicRoadmapConfig | null>(null);
  const [processedCompletions, setProcessedCompletions] = useState<Set<string>>(new Set());

  // Loading progress state
  const [loadingProgress, setLoadingProgress] = useState<string>('');
  const [loadingTime, setLoadingTime] = useState<number>(0);

  // Scroll state for auto-scroll button
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);


  // Plan Q&A Tab state
  const [activeTab, setActiveTab] = useState<ConversationTab>('general');
  const [planContext, setPlanContext] = useState<PlanQaContext | null>(null);
  const [hasPlanQaMessages, setHasPlanQaMessages] = useState(false);

  // Memory system state
  const [petName, setPetName] = useState<string>('');
  const [petType, setPetType] = useState<'dog' | 'cat' | 'both'>('both');

  // Sticker statistics state
  const [showStickerStats, setShowStickerStats] = useState(false);

  // Memoized handlers for StickerStats
  const handleCloseStickerStats = useCallback(() => {
    setShowStickerStats(false);
  }, []);

  const handleOpenStickerStats = useCallback(() => {
    setShowStickerStats(true);
  }, []);

  // Use user's preferred language for sticker stats, falling back to 'en'
  const stickerStatsLanguage = (user?.language || 'en') as 'zh-CN' | 'es' | 'fr' | 'en';


  // Use custom hook for pet context extraction
  const { extractPetInformation } = usePetContext();


  // Function to generate conversation memory summary
  const generateConversationMemory = useCallback(async (messages: Message[]) => {
    if (!user || !conversationId || messages.length < 2) return;

    const petInfo = extractPetInformation(messages);
    if (!petInfo.petName) return; // Only generate memory if we have a pet name

    console.log('🧠 Generating conversation memory for pet:', petInfo.petName);

    try {
      // Detect language from messages
      const conversationText = messages.map(msg => msg.content).join(' ');
      const isChinese = /[\u4e00-\u9fff]/.test(conversationText);
      const isSpanish = /[áéíóúñü]/.test(conversationText.toLowerCase());
      const isFrench = /[àâäéèêëïîôöùûüÿç]/.test(conversationText.toLowerCase());
      const language = isChinese ? 'zh-CN' : isSpanish ? 'es' : isFrench ? 'fr' : 'en';

      const memoryRequest: ConversationMemoryRequest = {
        conversationId,
        userId: user.uid,
        petName: petInfo.petName,
        petType: petInfo.petType,
        messages: messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: { toDate: () => msg.createdAt } as any
        })),
        language,
        context: {
          petBreed: undefined, // Could be extracted from conversation
          petAge: undefined,   // Could be extracted from conversation
          healthIssues: [],   // Could be extracted from conversation
          trainingGoals: []   // Could be extracted from conversation
        }
      };

      const result = await recentConversationMemoryService.generateConversationSummary(memoryRequest);
      if (result.success) {
        console.log('✅ Conversation memory generated successfully');
        // Update pet info state
        setPetName(petInfo.petName);
        setPetType(petInfo.petType);
      } else {
        console.error('❌ Failed to generate conversation memory:', result.error);
      }
    } catch (error) {
      console.error('❌ Error generating conversation memory:', error);
    }
  }, [user, conversationId]); // Removed extractPetInformation to prevent infinite loop


  // Function to check if conversation has Plan Q&A messages and restore context
  const checkForPlanQaMessages = useCallback((messages: Message[]) => {
    // Check if any message has planContext (which indicates it's a Plan Q&A message)
    const planQaMessages = messages.filter(message => message.planContext !== undefined);
    const hasPlanQa = planQaMessages.length > 0;

    console.log('🔍 Checking for Plan Q&A messages:', {
      totalMessages: messages.length,
      hasPlanQa,
      activeTab,
      planQaMessages: planQaMessages.length
    });

    setHasPlanQaMessages(hasPlanQa);

    // If we have Plan Q&A messages but no plan context, restore it from the latest day message
    if (hasPlanQa && !planContext) {
      // Find the message with the highest day number (latest day)
      const latestPlanQaMessage = planQaMessages.reduce((latest, current) => {
        if (!latest.planContext || !current.planContext) return latest;
        return current.planContext.day > latest.planContext.day ? current : latest;
      });

      if (latestPlanQaMessage.planContext) {
        console.log('🔄 Restoring plan context from latest day message:', latestPlanQaMessage.planContext);
        setPlanContext({
          planId: latestPlanQaMessage.planContext.planId,
          day: latestPlanQaMessage.planContext.day,
          contextType: latestPlanQaMessage.planContext.contextType,
          contextElement: latestPlanQaMessage.planContext.contextElement,
          planTitle: `Day ${latestPlanQaMessage.planContext.day}`,
          planDescription: ''
        });
      }
    }

    // If we're on Plan Q&A tab but no longer have a roadmap or Plan Q&A messages, switch to general
    if (activeTab === 'planQa' && !currentRoadmap && !hasPlanQa) {
      console.log('🔄 Switching from Plan Q&A to General Chat - no roadmap and no Plan Q&A messages found');
      setActiveTab('general');
      setPlanContext(null);
    }
  }, [activeTab, planContext, currentRoadmap]);

  // Multi-language loading progress function
  // Now using utility directly in effect equivalent


  // Multi-language retry message function
  // Now using utility directly where needed


  // Progress tracking effect
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isTyping || isLoading) {
      const startTime = Date.now();
      interval = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        setLoadingTime(elapsed);

        // Update progress messages based on time with multi-language support
        const language = detectLanguageFromMessages(messages, user?.language);
        if (elapsed < 10) {
          setLoadingProgress(getLoadingProgressText('thinking', language));
        } else if (elapsed < 30) {
          setLoadingProgress(getLoadingProgressText('processing', language));
        } else if (elapsed < 60) {
          setLoadingProgress(getLoadingProgressText('working', language));
        } else {
          setLoadingProgress(getLoadingProgressText('takingLonger', language));
        }
      }, 1000);
    } else {
      setLoadingTime(0);
      setLoadingProgress('');
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTyping, isLoading, messages, user?.language]);

  // Reset plan context when conversation changes
  useEffect(() => {
    setPlanContext(null);
    setActiveTab('general');
    setHasPlanQaMessages(false);
  }, [conversationId]);

  // Check for Plan Q&A messages when messages change
  useEffect(() => {
    checkForPlanQaMessages(messages);
  }, [messages, checkForPlanQaMessages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const messagesContainer = document.querySelector('[data-messages-container]');
    attemptAutoScroll(messagesContainer);
  }, [messages]);

  // Auto-scroll when typing indicator appears/disappears
  useEffect(() => {
    const messagesContainer = document.querySelector('[data-messages-container]');
    if (messagesContainer) {
      setTimeout(() => {
        scrollToBottom(messagesContainer);
      }, 100);
    }
  }, [isTyping, isLoading]);

  // Auto-scroll when conversational flow state changes
  useEffect(() => {
    const messagesContainer = document.querySelector('[data-messages-container]');
    if (messagesContainer) {
      setTimeout(() => {
        scrollToBottom(messagesContainer);
      }, 100);
    }
  }, [isInConversationalFlow, conversationalStep]);

  // Handle scroll events to show/hide scroll-to-bottom button
  useEffect(() => {
    const messagesContainer = document.querySelector('[data-messages-container]');
    if (messagesContainer) {
      const handleScroll = () => {
        // Check if NOT near bottom (inverted logic for showing button)
        // original logic: scrollTop < scrollHeight - clientHeight - 100;
        // utility: isNearBottom returns true if >= ...
        // so isScrolledUp = !isNearBottom
        const isScrolledUp = !isNearBottom(messagesContainer, 100);
        setShowScrollToBottom(isScrolledUp);
      };

      messagesContainer.addEventListener('scroll', handleScroll);
      return () => messagesContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);


  // Thread is now managed by useThreadManagement hook


  // Messages are now managed by useChatMessages hook
  // Extract pet information and generate memory when messages change
  useEffect(() => {
    if (messages.length >= 2) {
      const petInfo = extractPetInformation(messages);
      if (petInfo.petName) {
        setPetName(petInfo.petName);
        setPetType(petInfo.petType);
        // Generate memory for existing conversation
        generateConversationMemory(messages);
      }
    }

    // Check for existing completion reflections and mark them as processed
    const existingCompletions = new Set<string>();
    messages.forEach(message => {
      if (message.role === 'assistant' &&
        message.content.includes('Completion Reflection Conversation')) {
        // Extract roadmap title from the reflection message
        const titleMatch = message.content.match(/completing "([^"]+)"/i);
        if (titleMatch) {
          const roadmapTitle = titleMatch[1];
          existingCompletions.add(roadmapTitle);
        }
      }
    });

    if (existingCompletions.size > 0) {
      console.log('📋 Found existing completion reflections:', Array.from(existingCompletions));
      setProcessedCompletions(existingCompletions);
    }
  }, [messages]); // Removed function dependencies to prevent infinite loop







  // Use custom hook for semantic intent analysis
  const { analyzeSummaryRequest } = useSemanticIntent();


  const handleSendMessage = async (e: React.FormEvent) => {
    console.log('handleSendMessage called');
    e.preventDefault();

    if (!newMessage.trim() || !user || !conversationId || !threadId) {
      console.log('Early return conditions:', {
        hasMessage: !!newMessage.trim(),
        hasUser: !!user,
        hasConversationId: !!conversationId,
        hasThreadId: !!threadId
      });
      return;
    }


    // Check if we're in conversational flow
    if (isInConversationalFlow) {
      await handleConversationalResponse(newMessage);
      return;
    }

    // Check if we're in completion reflection flow
    if (isInCompletionReflection && completedRoadmap) {
      await handleCompletionReflectionResponse(newMessage);
      return;
    }

    setError(null);
    setIsLoading(true);
    setIsTyping(true);

    try {
      // Add user message
      const userMessage = {
        content: newMessage,
        userId: user.uid,
        createdAt: new Date(),
        role: 'user' as const,
      };

      await addDoc(collection(db, `conversations/${conversationId}/messages`), userMessage);

      // ✅ Track message sent
      try {
        const currentMessageCount = messages.length + 1;
        // Check if message contains pet name (simple check for now)
        const containsPetName = false; // We'll enhance this later with actual pet profile check

        await analyticsService.trackMessageSent(
          user.uid,
          conversationId,
          currentMessageCount,
          containsPetName
        );
        console.log('📊 Analytics: Message sent tracked');
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }

      // Update conversation title if this is the first message
      if (messages.length === 0) {
        await updateConversationTitle(newMessage);
      }

      const currentMessage = newMessage;
      setNewMessage('');

      // Detect language first
      const detectedLanguage = /[\u4e00-\u9fff]/.test(currentMessage) ? 'zh-CN' :
        /[áéíóúñü]/.test(currentMessage.toLowerCase()) ? 'es' :
          /[àâäéèêëïîôöùûüÿç]/.test(currentMessage.toLowerCase()) ? 'fr' : 'en';

      // Define conversation context early for parallel fetching
      const conversationContext = messages
        .map(msg => `${msg.role}: ${msg.content}`)
        .join('\n\n');

      // FIRST: Check if this is a summary request using semantic analysis
      // AND get memory context in parallel to save time
      console.log('🚀 Step 1: Starting parallel execution of Intent Analysis & Memory Context Fetching');
      console.time('Parallel_Intent_And_Memory');

      const [summaryAnalysis, enhancedContextResult] = await Promise.all([
        analyzeSummaryRequest(currentMessage, detectedLanguage),
        roadmapContextService.getRoadmapContext(
          user?.uid || '',
          currentMessage,
          conversationContext
        ).catch(error => {
          console.error('❌ Error fetching memory context in parallel:', error);
          // Return fallback context on error
          return {
            hasPetContext: false,
            hasConversationHistory: false,
            detectedPetName: null,
            petProfiles: [],
            recentSummaries: [],
            petContextString: '',
            conversationHistoryContext: '',
            conversationContext: conversationContext,
            userQuestion: currentMessage
          };
        })
      ]);

      console.timeEnd('Parallel_Intent_And_Memory');
      console.log('✅ Step 1 Complete: Parallel execution finished');

      const isSummaryRequest = summaryAnalysis.isSummary;
      const detectedPetName = summaryAnalysis.petName;

      // Store the fetched context to use later (renaming for consistent usage)
      // We need to use 'let' because we might modify it later or fallback logic might need to assign to it if it wasn't defined here
      let enhancedContext = enhancedContextResult || {
        hasPetContext: false,
        hasConversationHistory: false,
        detectedPetName: null,
        petProfiles: [],
        recentSummaries: [],
        petContextString: '',
        conversationHistoryContext: '',
        conversationContext: conversationContext,
        userQuestion: currentMessage
      };

      console.log('📊 Summary analysis result:', {
        isSummaryRequest,
        detectedPetName,
        confidence: summaryAnalysis.confidence,
        intent: summaryAnalysis.intent,
        message: currentMessage.substring(0, 50) + '...'
      });

      console.log('🧠 Memory context retrieved in parallel:', {
        hasPetContext: enhancedContext.hasPetContext,
        hasConversationHistory: enhancedContext.hasConversationHistory,
        detectedPetName: enhancedContext.detectedPetName,
        petProfilesCount: enhancedContext.petProfiles.length,
        summariesCount: enhancedContext.recentSummaries.length
      });

      // Initialize roadmap detection variables
      let shouldProceedWithRoadmap = false;
      let roadmapAnalysis = null;

      // Check if this is a roadmap request first (even if it was detected as summary)
      if (summaryAnalysis.intent === 'create_roadmap') {
        console.log('✅ Roadmap request detected in semantic analysis, proceeding to roadmap generation...');
        // Set roadmap flags immediately
        shouldProceedWithRoadmap = true;
        // Debug duration extraction
        console.log('🔍 Debug duration extraction:', {
          originalDuration: summaryAnalysis.entities?.duration,
          extractedNumber: summaryAnalysis.entities?.duration?.replace(/[^\d]/g, ''),
          parsedDuration: parseInt(summaryAnalysis.entities?.duration?.replace(/[^\d]/g, '') || '7')
        });

        roadmapAnalysis = {
          confidence: summaryAnalysis.confidence,
          roadmapType: summaryAnalysis.entities?.roadmapType || 'general',
          suggestedDuration: parseInt(summaryAnalysis.entities?.duration?.replace(/[^\d]/g, '') || '7'),
          difficulty: (summaryAnalysis.entities?.difficulty as 'beginner' | 'intermediate' | 'advanced') || 'beginner'
        };
        console.log('📊 Roadmap analysis created from semantic detection:', roadmapAnalysis);
      } else if (isSummaryRequest) {
        console.log('✅ Summary request detected, proceeding to summary generation...');
        // Continue to normal AI response flow with summary handling
      } else {
        console.log('❌ Not a summary or roadmap request, checking for roadmap need...');
      }

      // Fast-path: if it's a confident general question, skip roadmap checks entirely
      const isConfidentGeneralQuestion = summaryAnalysis.intent === 'general_question' && summaryAnalysis.confidence >= 0.75;
      if (isConfidentGeneralQuestion) {
        console.log('⚡ Fast-path: Confident general question detected, skipping roadmap detection');
      }

      // Check for roadmap need BEFORE getting AI response (only if not a summary request and not fast-pathed)
      console.log('Checking for roadmap need...');
      // conversationContext is already defined above


      // Enhanced roadmap detection with more flexible context analysis
      // More flexible keyword detection - split into individual words and phrases
      const roadmapActionWords = [
        'create', 'make', 'give', 'need', 'want', 'help', 'design', 'develop', 'build', 'establish',
        '制定', '创建', '给我', '需要', '想要', '帮助', '建立', '设计', '开发', '制定',
        'crear', 'hacer', 'dame', 'necesito', 'quiero', 'ayudar', 'diseñar',
        'créer', 'faire', 'donnez-moi', 'j\'ai besoin', 'aider', 'concevoir'
      ];

      const roadmapObjectWords = [
        'plan', 'roadmap', 'schedule', 'program', 'guide', 'strategy', 'approach',
        '计划', '路线图', '方案', '指南', '训练', '练习', '学习',
        'plan', 'hoja de ruta', 'programa', 'guía', 'entrenamiento', 'práctica',
        'plan', 'feuille de route', 'programme', 'guide', 'entraînement', 'pratique'
      ];

      const descriptiveWords = [
        'personalized', 'customized', 'tailored', 'specific', 'detailed', 'structured',
        '个性化', '定制', '具体', '详细', '结构化',
        'personalizado', 'adaptado', 'específico', 'detallado', 'estructurado',
        'personnalisé', 'adapté', 'spécifique', 'détaillé', 'structuré'
      ];

      // Enhanced detection logic - more flexible matching
      const messageLower = currentMessage.toLowerCase();

      // Check for exact phrase matches (original logic)
      const exactPhraseMatches = [
        'create a plan', 'make a plan', 'give me a plan', 'need a plan',
        'create a roadmap', 'make a roadmap', 'give me a roadmap', 'need a roadmap',
        'create a schedule', 'make a schedule', 'give me a schedule', 'need a schedule',
        'create a program', 'make a program', 'give me a program', 'need a program',
        '制定计划', '创建计划', '给我计划', '需要计划',
        '制定路线图', '创建路线图', '给我路线图', '需要路线图',
        'crear un plan', 'hacer un plan', 'dame un plan', 'necesito un plan',
        'crear una hoja de ruta', 'hacer una hoja de ruta', 'dame una hoja de ruta',
        'créer un plan', 'faire un plan', 'donnez-moi un plan', 'j\'ai besoin d\'un plan',
        'créer une feuille de route', 'faire une feuille de route', 'donnez-moi une feuille de route'
      ];

      // Additional pattern matching for more complex phrases
      const complexPhrasePatterns = [
        // Chinese patterns for complex requests
        /创建.*?计划/i,  // 创建一个...计划
        /制定.*?计划/i,  // 制定一个...计划
        /给我.*?计划/i,  // 给我一个...计划
        /需要.*?计划/i,  // 需要一个...计划
        /创建.*?路线图/i, // 创建一个...路线图
        /制定.*?路线图/i, // 制定一个...路线图
        /创建.*?方案/i,   // 创建一个...方案
        /制定.*?方案/i,   // 制定一个...方案
        // English patterns for complex requests
        /create.*?plan/i,
        /make.*?plan/i,
        /give.*?plan/i,
        /need.*?plan/i,
        /create.*?roadmap/i,
        /make.*?roadmap/i,
        /create.*?schedule/i,
        /make.*?schedule/i,
        // Spanish patterns
        /crear.*?plan/i,
        /hacer.*?plan/i,
        /dame.*?plan/i,
        /necesito.*?plan/i,
        // French patterns
        /créer.*?plan/i,
        /faire.*?plan/i,
        /donnez.*?plan/i,
        /j'ai besoin.*?plan/i
      ];

      const hasExactPhrase = exactPhraseMatches.some(phrase =>
        messageLower.includes(phrase)
      );

      // Check for complex phrase patterns
      const hasComplexPhrase = complexPhrasePatterns.some(pattern =>
        pattern.test(currentMessage)
      );

      // Check for flexible combinations (action + object)
      const hasActionWord = roadmapActionWords.some(word =>
        messageLower.includes(word)
      );
      const hasObjectWord = roadmapObjectWords.some(word =>
        messageLower.includes(word)
      );
      const hasDescriptiveWord = descriptiveWords.some(word =>
        messageLower.includes(word)
      );

      // More flexible detection: either exact phrase OR complex phrase OR combination of action + object
      const hasExplicitRequest = hasExactPhrase ||
        hasComplexPhrase ||
        (hasActionWord && hasObjectWord) ||
        (hasActionWord && hasDescriptiveWord) ||
        (hasObjectWord && hasDescriptiveWord);

      // Enhanced debug logging for detection analysis
      const matchedActionWords = roadmapActionWords.filter(word => messageLower.includes(word));
      const matchedObjectWords = roadmapObjectWords.filter(word => messageLower.includes(word));
      const matchedDescriptiveWords = descriptiveWords.filter(word => messageLower.includes(word));

      console.log('🚀 Explicit Request Detection Analysis:', {
        message: currentMessage,
        messageLower,
        hasExactPhrase,
        hasComplexPhrase,
        hasActionWord,
        hasObjectWord,
        hasDescriptiveWord,
        hasExplicitRequest,
        matchedActionWords,
        matchedObjectWords,
        matchedDescriptiveWords,
        detectionLogic: {
          'exactPhrase': hasExactPhrase,
          'complexPhrase': hasComplexPhrase,
          'action+object': hasActionWord && hasObjectWord,
          'action+descriptive': hasActionWord && hasDescriptiveWord,
          'object+descriptive': hasObjectWord && hasDescriptiveWord
        }
      });

      // Check for roadmap requests first (regardless of summary status)
      // Variables already declared above

      // PRIORITY FALLBACK: Check for roadmap keywords FIRST (before old system)
      if (!shouldProceedWithRoadmap && !isSummaryRequest && !isConfidentGeneralQuestion) {
        console.log('🔍 PRIORITY: Checking for roadmap keywords in message:', currentMessage);

        // Check for roadmap keywords in the message
        const roadmapKeywords = [
          '训练计划', '训练方案', '训练教程', '行为改善', '行为修正', '如厕训练',
          'training plan', 'training program', 'behavior improvement', 'housebreaking'
        ];

        const hasRoadmapKeywords = roadmapKeywords.some(keyword =>
          currentMessage.toLowerCase().includes(keyword.toLowerCase())
        );

        console.log('🔍 PRIORITY: Keyword detection result:', {
          hasRoadmapKeywords,
          message: currentMessage,
          keywords: roadmapKeywords
        });

        if (hasRoadmapKeywords) {
          console.log('✅ PRIORITY: Roadmap keywords detected, proceeding with fallback detection...');

          // Extract duration from message (simple regex)
          const durationMatch = currentMessage.match(/(\d+)天|(\d+)\s*days?/i);
          const extractedDuration = durationMatch ?
            parseInt(durationMatch[1] || durationMatch[2]) : 7;

          console.log('🔍 PRIORITY Fallback duration extraction:', {
            message: currentMessage,
            durationMatch: durationMatch,
            extractedDuration: extractedDuration
          });

          roadmapAnalysis = {
            confidence: 0.8,
            roadmapType: 'general',
            suggestedDuration: extractedDuration,
            difficulty: 'beginner' as const
          };
          shouldProceedWithRoadmap = true;
          console.log('✅ PRIORITY: Roadmap analysis set, skipping old system');
        }
      }

      // Only proceed with additional roadmap detection if not already detected
      if (!shouldProceedWithRoadmap && !isSummaryRequest && !isConfidentGeneralQuestion) {
        // Use hybrid roadmap detection (semantic + keyword fallback)
        console.log('🔍 Running hybrid roadmap detection...');
        const roadmapDetection = await semanticIntentClassificationService.detectRoadmapRequest(currentMessage);
        console.log('🎯 Hybrid roadmap detection result:', roadmapDetection);

        if (roadmapDetection.isRoadmap) {
          console.log(`✅ Roadmap detected via ${roadmapDetection.method} method (confidence: ${roadmapDetection.confidence})`);
          console.log('📊 Extracted entities:', roadmapDetection.entities);

          // Create roadmap analysis result from semantic detection
          // Debug duration extraction
          console.log('🔍 Debug duration extraction (hybrid):', {
            originalDuration: roadmapDetection.entities.duration,
            extractedNumber: roadmapDetection.entities.duration?.replace(/[^\d]/g, ''),
            parsedDuration: parseInt(roadmapDetection.entities.duration?.replace(/[^\d]/g, '') || '7')
          });

          roadmapAnalysis = {
            confidence: roadmapDetection.confidence,
            roadmapType: roadmapDetection.entities.roadmapType || 'general',
            suggestedDuration: parseInt(roadmapDetection.entities.duration?.replace(/[^\d]/g, '') || '7'),
            difficulty: (roadmapDetection.entities.difficulty as 'beginner' | 'intermediate' | 'advanced') || 'beginner'
          };
          shouldProceedWithRoadmap = true;
        } else {
          // Fallback to original keyword-based analysis (only if not already detected)
          if (!shouldProceedWithRoadmap) {
            console.log('❌ No roadmap detected via semantic classification, trying keyword fallback...');
            roadmapAnalysis = analyzeConversationForRoadmap(currentMessage, conversationContext);
            console.log('Roadmap analysis result (keyword fallback):', roadmapAnalysis);

            const hasStrongContext = roadmapAnalysis.confidence >= 0.6;
            shouldProceedWithRoadmap = roadmapAnalysis.shouldSuggestRoadmap || hasExplicitRequest || hasStrongContext;
          } else {
            console.log('✅ Roadmap already detected by priority fallback, skipping old system');
          }
        }
      }

      if (shouldProceedWithRoadmap) {
        if (hasExplicitRequest) {
          // For explicit requests, generate roadmap directly without conversational flow
          console.log('Explicit roadmap request detected, generating directly...');
          setIsLoading(true);


          try {
            await generateDynamicRoadmap(currentMessage, conversationContext, roadmapAnalysis!, enhancedContext);
            console.log('Direct roadmap generation completed');
          } catch (error) {
            console.error('Error in direct roadmap generation:', error);
            setError(t('errorGeneratingRoadmap'));
          } finally {
            setIsLoading(false);
          }
        } else {
          // Start conversational flow for implicit roadmap suggestions
          console.log('Initiating conversational roadmap approach...');
          setConversationalQuestion(currentMessage);
          setConversationalContext(conversationContext);
          setIsInConversationalFlow(true);
          setConversationalStep(0);

          // Generate and send the first conversational step (Listen) using GPT
          console.log('Starting conversational flow with GPT-generated Listen step');
          const listenPrompt = generateConversationalStepPrompt('listen', currentMessage, conversationContext, 0);

          setIsLoading(true);
          const gptListenResponse = await sendMessageToThread(threadId, listenPrompt);
          const cleanedListenResponse = cleanAIResponse(gptListenResponse);
          setIsLoading(false);

          await addDoc(collection(db, `conversations/${conversationId}/messages`), {
            content: cleanedListenResponse,
            userId: 'assistant',
            createdAt: new Date(),
            role: 'assistant' as const,
          });

          // Update conversation context with the listen response
          setConversationalContext(conversationContext + `\nassistant: ${cleanedListenResponse}`);
        }
      } else {
        // Normal AI response flow with memory integration
        console.log('No roadmap needed, proceeding with normal AI response with memory integration...');

        // Detect language from current message and conversation context
        const isChinese = /[\u4e00-\u9fff]/.test(currentMessage);
        const isSpanish = /[áéíóúñü]/.test(currentMessage.toLowerCase());
        const isFrench = /[àâäéèêëïîôöùûüÿç]/.test(currentMessage.toLowerCase());

        const detectedLanguage = isChinese ? 'Chinese' : isSpanish ? 'Spanish' : isFrench ? 'French' : 'English';

        // Get enhanced context with memory integration (same as roadmap generation)
        console.log('🧠 Retrieving memory context for normal AI response...');
        // Get enhanced context with memory integration (same as roadmap generation)
        console.log('🧠 Using pre-fetched memory context for normal AI response...');
        // enhancedContext is already fetched in parallel at the beginning of the function


        // Create memory-enhanced prompt for normal AI responses
        // Use the summary analysis results from earlier
        console.log('📊 Using summary analysis results from earlier:', {
          isSummaryRequest,
          detectedPetName,
          confidence: summaryAnalysis.confidence,
          hasConversationHistory: enhancedContext.hasConversationHistory,
          hasPetContext: enhancedContext.hasPetContext
        });

        const memoryEnhancedPrompt = isSummaryRequest ?
          `You are a pet care assistant helping a pet owner understand their recent conversations about their pet.

IMPORTANT: The user is communicating in ${detectedLanguage}. You MUST respond ONLY in ${detectedLanguage}. Be warm, understanding, and professional.

User's request: "${currentMessage}"

${enhancedContext.hasConversationHistory ? `
Based on our conversation history from the last 3 months about ${detectedPetName || enhancedContext.detectedPetName || 'your pet'}:
${enhancedContext.conversationHistoryContext}

Please provide a comprehensive summary structured as follows:

**主要话题 (Main Topics):**
- List the key topics we've discussed

**护理建议 (Care Advice):**
- Specific pet care advice provided

**训练进展 (Training Progress):**
- Training progress and any challenges

**健康观察 (Health Observations):**
- Health notes and observations

**下一步建议 (Next Steps):**
- Recommendations for continued care

Use bullet points or numbered lists for better readability.` : ''}

${enhancedContext.hasPetContext ? `
Pet profile information for context:
${enhancedContext.petContextString}
` : ''}

${!enhancedContext.hasConversationHistory ? `
I don't have access to recent conversation history at the moment. Please let me know what specific aspects of your pet's care you'd like me to help summarize or discuss.
` : ''}

Respond directly with a detailed, structured summary without any prefix like "Pet Care Assistant:" or "Assistant:".` :
          `You are a pet care assistant helping a pet owner with their dog or cat care concern.

IMPORTANT: The user is communicating in ${detectedLanguage}. You MUST respond ONLY in ${detectedLanguage}. Be warm, understanding, and professional.

User's message: "${currentMessage}"

Current conversation context:
${conversationContext}

${enhancedContext.hasConversationHistory ? `
Recent conversation history about ${enhancedContext.detectedPetName || 'your pet'}:
${enhancedContext.conversationHistoryContext}
` : ''}

${enhancedContext.hasPetContext ? `
Pet profile information:
${enhancedContext.petContextString}
` : ''}

Respond directly without any prefix like "Pet Care Assistant:" or "Assistant:". Start your response immediately with your message.`;

        console.log('📝 Memory-enhanced prompt created:', {
          hasMemoryHistory: enhancedContext.hasConversationHistory,
          hasPetProfile: enhancedContext.hasPetContext,
          promptLength: memoryEnhancedPrompt.length,
          detectedPetName: enhancedContext.detectedPetName,
          isSummaryRequest: isSummaryRequest,
          conversationHistoryLength: enhancedContext.conversationHistoryContext.length,
          petContextLength: enhancedContext.petContextString.length
        });

        // Debug: Log the actual memory content being used
        if (enhancedContext.hasConversationHistory) {
          console.log('🧠 Memory context being used:', {
            conversationHistory: enhancedContext.conversationHistoryContext.substring(0, 200) + '...',
            petContext: enhancedContext.petContextString.substring(0, 200) + '...'
          });
        }

        // Get AI response using the persistent thread with memory-enhanced prompt
        const aiResponse = await sendMessageToThread(threadId, memoryEnhancedPrompt);

        // Clean the AI response before saving
        const cleanedResponse = cleanAIResponse(aiResponse);

        // Add AI message
        await addDoc(collection(db, `conversations/${conversationId}/messages`), {
          content: cleanedResponse,
          userId: 'assistant',
          createdAt: new Date(),
          role: 'assistant' as const,
        });

        // Update conversation with last message for normal AI responses
        await updateDoc(doc(db, `conversations/${conversationId}`), {
          lastMessage: cleanedResponse.substring(0, 100),
          updatedAt: new Date(),
        });

        // Trigger conversation categorization after AI response
        await triggerCategorization();

        // Generate conversation memory after AI response
        const updatedMessages = [...messages, { ...userMessage, id: 'temp' }];
        await generateConversationMemory(updatedMessages);
      }

      // For conversational flow, update conversation with user message
      if (roadmapAnalysis && roadmapAnalysis.shouldSuggestRoadmap) {
        await updateDoc(doc(db, `conversations/${conversationId}`), {
          lastMessage: currentMessage.substring(0, 100),
          updatedAt: new Date(),
        });
      }
    } catch (error) {
      console.error('Error in chat:', error);

      // Determine error type and send appropriate retry message
      let errorType: 'timeout' | 'api' | 'network' = 'api';

      if (error instanceof Error) {
        const errorMessage = error.message;
        console.error('Error details:', {
          message: errorMessage,
          stack: error.stack
        });

        if (errorMessage.includes('timed out') || errorMessage === 'TIMEOUT') {
          errorType = 'timeout';
        } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
          errorType = 'network';
        }
      }

      // Send retry message as AI response instead of showing error
      const retryMessage = {
        content: getRetryMessage(errorType),
        userId: 'assistant',
        createdAt: new Date(),
        role: 'assistant' as const,
      };

      await addDoc(collection(db, `conversations/${conversationId}/messages`), retryMessage);

      // Update conversation with retry message
      await updateDoc(doc(db, `conversations/${conversationId}`), {
        lastMessage: retryMessage.content.substring(0, 100),
        updatedAt: new Date(),
      });
    } finally {
      console.log('Message process completed, setting isLoading to false');
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleEditMessage = async (messageId: string, newContent: string) => {
    try {
      await updateDoc(doc(db, `conversations/${conversationId}/messages`, messageId), {
        content: newContent,
      });
      setEditingMessage(null);
    } catch (error) {
      console.error('Error editing message:', error);
      if (error instanceof Error) {
        setError(t('errorEditingMessageWithDetails'));
      }
    }
  };



  // cleanAIResponse is now imported from ../utils/messageUtils

  // Trigger conversation categorization
  const triggerCategorization = async () => {
    try {
      // Check if conversation already has hashtags
      const conversationRef = doc(db, `conversations/${conversationId}`);
      const conversationDoc = await getDoc(conversationRef);

      if (conversationDoc.exists()) {
        const conversationData = conversationDoc.data();

        // Only categorize if no hashtags exist and we have enough messages
        if (!conversationData.hashtags && messages.length >= 1) {
          const conversationContent = messages
            .map(msg => `${msg.role}: ${msg.content}`)
            .join('\n\n');

          // Get hashtags from OpenAI with language detection
          const hashtags = await categorizeConversation(conversationContent);

          // Update conversation with hashtags
          await updateDoc(conversationRef, {
            hashtags: hashtags,
            updatedAt: new Date(),
          });

          console.log('Conversation categorized with language-aware hashtags:', hashtags);
        }
      }
    } catch (error) {
      console.error('Error categorizing conversation:', error);
    }
  };

  // New function to handle return to chat view
  const handleReturnToChat = () => {
    // Use the direct return function if provided, otherwise fall back to toggle
    if (onReturnToChat) {
      onReturnToChat();
    } else {
      onRoadmapViewToggle(null);
    }
  };




  // Handle roadmap completion reflection
  const handleRoadmapCompleted = async (roadmap: DynamicRoadmapConfig) => {
    console.log('🎉 ROADMAP COMPLETED - Starting reflection flow:', roadmap.title);

    // Check if threadId is available
    if (!threadId) {
      console.error('❌ ThreadId not available for completion reflection');
      setError(t('errorThreadNotInitialized'));
      return;
    }

    // Check if we've already processed this roadmap completion
    if (processedCompletions.has(roadmap.id)) {
      console.log('⚠️ Completion reflection already processed for this roadmap, skipping...');
      return;
    }

    // Check if we're already in completion reflection for this roadmap
    if (isInCompletionReflection && completedRoadmap?.id === roadmap.id) {
      console.log('⚠️ Completion reflection already in progress for this roadmap, skipping...');
      return;
    }

    // Check if we've already generated a completion reflection for this roadmap in messages
    const existingReflection = messages.find(m =>
      m.role === 'assistant' &&
      m.content.includes('Completion Reflection Conversation') &&
      m.content.includes(roadmap.title)
    );

    if (existingReflection) {
      console.log('⚠️ Completion reflection already exists for this roadmap, marking as processed...');
      setProcessedCompletions(prev => new Set(prev).add(roadmap.id));
      return;
    }

    setCompletedRoadmap(roadmap);
    setIsInCompletionReflection(true);

    // Find the original user question from the conversation
    const originalQuestion = messages
      .filter(m => m.role === 'user')
      .slice(-1)[0]?.content || 'General parenting concern';

    // Collect Plan Q&A history from messages with planContext
    const planQaHistory = messages
      .filter(m => m.planContext && m.planContext.planId === roadmap.id)
      .map(m => ({
        day: m.planContext!.day,
        contextType: m.planContext!.contextType,
        contextElement: m.planContext!.contextElement || '',
        userQuestion: m.role === 'user' ? m.content : '',
        assistantResponse: m.role === 'assistant' ? m.content : '',
        timestamp: m.createdAt
      }))
      .filter(qa => qa.userQuestion || qa.assistantResponse);

    console.log('📊 Plan Q&A History collected:', planQaHistory);

    // Generate completion reflection prompt with comprehensive data
    const userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = roadmap.userLanguage || 'en';
    const reflectionPrompt = generateCompletionReflectionPrompt(
      roadmap,
      originalQuestion,
      messages.map(m => `${m.role}: ${m.content}`).join('\n'),
      userLanguage,
      planQaHistory
    );

    try {
      // Send the reflection prompt to the assistant
      const aiResponse = await sendMessageToThread(threadId, reflectionPrompt);

      // Add the AI response to the database
      const aiMessage = {
        content: aiResponse,
        userId: 'assistant',
        createdAt: new Date(),
        role: 'assistant' as const,
      };

      await addDoc(collection(db, `conversations/${conversationId}/messages`), aiMessage);

      // Mark this roadmap as processed to prevent duplicate reflections
      setProcessedCompletions(prev => new Set(prev).add(roadmap.id));

      // After adding the reflection message, scroll to show the beginning of the reflection conversation
      setTimeout(() => {
        // Scroll to the last message (which should be the reflection message)
        const messagesContainer = document.querySelector('[data-messages-container]');

        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
          console.log('📜 Scrolled to show reflection conversation');
        }
      }, 500);

      // Also ensure the roadmap view shows the last day's card
      if (onRoadmapViewToggle) {
        console.log('🔄 ensuring roadmap view is open...');

        // Only toggle if not already open with this roadmap
        // This prevents closing the roadmap if it's already open (toggle behavior)
        if (!showRoadmapView || currentRoadmap?.id !== roadmap.id) {
          console.log('🔄 Opening roadmap view...');
          onRoadmapViewToggle(roadmap);
        } else {
          console.log('✅ Roadmap view already open, skipping toggle');
        }

        // Wait for the roadmap view to open (or use existing) and then scroll to the last day's card
        setTimeout(() => {
          console.log('🔍 Looking for roadmap container...');
          const roadmapContainer = document.querySelector('[data-roadmap-container]');

          if (roadmapContainer) {
            console.log('✅ Found roadmap container, scrolling to last day...');
            // Find the last day's card and scroll to it
            const totalDays = roadmap.dailyActivities.length;
            const lastDayCard = roadmapContainer.querySelector(`[data-day="${totalDays}"]`) ||
              roadmapContainer.querySelector(`[data-day-key="day-${totalDays}"]`) ||
              roadmapContainer.querySelector('[data-day="10"]') ||
              roadmapContainer.querySelector('div[class*="Accordion"]:last-child');

            if (lastDayCard) {
              lastDayCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
              console.log(`📜 Scrolled to show last day card (Day ${totalDays}) in roadmap`);
            } else {
              // Fallback: scroll to top of roadmap
              roadmapContainer.scrollTop = 0;
              console.log('📜 Scrolled to top of roadmap as fallback');
            }
          } else {
            console.log('❌ Roadmap container not found, trying again...');
            // Try again after a longer delay
            setTimeout(() => {
              const roadmapContainer = document.querySelector('[data-roadmap-container]');
              if (roadmapContainer) {
                console.log('✅ Found roadmap container on retry, scrolling to last day...');
                const totalDays = roadmap.dailyActivities.length;
                const lastDayCard = roadmapContainer.querySelector(`[data-day="${totalDays}"]`) ||
                  roadmapContainer.querySelector(`[data-day-key="day-${totalDays}"]`) ||
                  roadmapContainer.querySelector('[data-day="10"]') ||
                  roadmapContainer.querySelector('div[class*="Accordion"]:last-child');

                if (lastDayCard) {
                  lastDayCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  console.log(`📜 Scrolled to show last day card (Day ${totalDays}) in roadmap on retry`);
                } else {
                  roadmapContainer.scrollTop = 0;
                  console.log('📜 Scrolled to top of roadmap as fallback on retry');
                }
              } else {
                console.log('❌ Roadmap container still not found after retry');
              }
            }, 2000);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('❌ Error in handleRoadmapCompleted:', error);
      setError(t('errorGeneratingCompletionReflection'));
      setIsInCompletionReflection(false);
    }
  };

  // Handle user response during completion reflection
  const handleCompletionReflectionResponse = async (userResponse: string) => {
    console.log('📝 HANDLING COMPLETION REFLECTION RESPONSE:', userResponse);

    if (!completedRoadmap) return;

    // Check if threadId is available
    if (!threadId) {
      console.error('❌ ThreadId not available for completion reflection response');
      setError(t('errorProcessingReflectionResponse'));
      return;
    }

    // Store the user's reflection for follow-up plan suggestions
    // Note: userReflection state removed, but reflection is still processed

    try {
      // Add user message to database
      const userMessage = {
        content: userResponse,
        userId: user!.uid,
        createdAt: new Date(),
        role: 'user' as const,
      };

      await addDoc(collection(db, `conversations/${conversationId}/messages`), userMessage);
      setNewMessage('');

      // Generate follow-up plan suggestion prompt based on user reflection
      const userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = completedRoadmap.userLanguage || 'en';
      const followUpPrompt = generateFollowUpPlanSuggestionPrompt(
        completedRoadmap,
        userResponse,
        userLanguage
      );

      // Send the follow-up prompt to the assistant
      const aiResponse = await sendMessageToThread(threadId, followUpPrompt);

      // Add AI response to database
      const aiMessage = {
        content: aiResponse,
        userId: 'assistant',
        createdAt: new Date(),
        role: 'assistant' as const,
      };

      await addDoc(collection(db, `conversations/${conversationId}/messages`), aiMessage);

      // Reset completion reflection state
      setIsInCompletionReflection(false);
      setCompletedRoadmap(null);
    } catch (error) {
      console.error('❌ Error in handleCompletionReflectionResponse:', error);
      setError(t('errorProcessingReflectionResponseRetry'));
    }
  };

  // Handle conversational roadmap completion
  const handleConversationalRoadmapRequest = async (question: string, context: string, childAge?: number) => {
    try {
      console.log('User agreed to conversational roadmap, generating with enhanced context...');
      console.log('Enhanced conversation context:', context);

      // Now generate the roadmap since user has given permission
      // Use the enhanced context that includes the full conversational flow
      const roadmapAnalysis = analyzeConversationForRoadmap(question, context);
      await generateDynamicRoadmap(question, context, roadmapAnalysis);

      // End the conversational flow
      setIsInConversationalFlow(false);
      setConversationalStep(0);

      // Clear conversation context for next time
      setConversationalQuestion('');
      setConversationalContext('');
    } catch (error) {
      console.error('Error generating conversational roadmap:', error);
      setError(t('errorGeneratingRoadmap'));
    }
  };

  // checkUserAgreement and checkUserDecline are now imported from ../utils/languageUtils



  // generateConversationalStepPrompt is now imported from ../utils/chatUtils

  // Handle conversational flow responses
  const handleConversationalResponse = async (userResponse: string) => {
    try {
      // Add user message to chat
      const userMessage = {
        content: userResponse,
        userId: user!.uid,
        createdAt: new Date(),
        role: 'user' as const,
      };
      await addDoc(collection(db, `conversations/${conversationId}/messages`), userMessage);

      setNewMessage('');

      // Update conversation context with new user response
      const updatedContext = conversationalContext + `\nuser: ${userResponse}`;
      setConversationalContext(updatedContext);

      // Get conversational steps
      const conversationalSteps = generateConversationalRoadmapApproach(conversationalQuestion, updatedContext);
      console.log('Current conversational step:', conversationalStep, 'Total steps:', conversationalSteps.length);

      // Check if user is agreeing to roadmap at any point
      const userAgreed = checkUserAgreement(userResponse);
      const userDeclined = checkUserDecline(userResponse);

      if (userAgreed && conversationalStep >= 3) { // Can only agree after at least immediate guidance step
        console.log('User agreed to roadmap, generating...');
        await handleConversationalRoadmapRequest(conversationalQuestion, updatedContext);
        return;
      }

      if (userDeclined) {
        console.log('User declined roadmap, ending conversational flow');
        // Generate personalized decline response using GPT
        const declinePrompt = generateConversationalStepPrompt('continue_dialogue', conversationalQuestion, updatedContext, conversationalStep);
        const gptDeclineResponse = await sendMessageToThread(threadId!, declinePrompt);
        const cleanedDeclineResponse = cleanAIResponse(gptDeclineResponse);

        await addDoc(collection(db, `conversations/${conversationId}/messages`), {
          content: cleanedDeclineResponse,
          userId: 'assistant',
          createdAt: new Date(),
          role: 'assistant' as const,
        });

        setIsInConversationalFlow(false);
        setConversationalStep(0);
        return;
      }

      // Continue with next step in sequence
      const nextStepIndex = conversationalStep + 1;
      if (nextStepIndex < conversationalSteps.length) {
        console.log('Moving to step', nextStepIndex, 'of', conversationalSteps.length);
        const nextStep = conversationalSteps[nextStepIndex];

        // Generate personalized response using GPT Assistant API
        const stepPrompt = generateConversationalStepPrompt(nextStep.type, conversationalQuestion, updatedContext, nextStepIndex);
        console.log('Generating GPT response for step:', nextStep.type);

        setIsLoading(true);
        const gptResponse = await sendMessageToThread(threadId!, stepPrompt);
        const cleanedGptResponse = cleanAIResponse(gptResponse);
        setIsLoading(false);

        // Send the GPT-generated conversational step response
        await addDoc(collection(db, `conversations/${conversationId}/messages`), {
          content: cleanedGptResponse,
          userId: 'assistant',
          createdAt: new Date(),
          role: 'assistant' as const,
        });

        // Update conversation context with assistant response
        setConversationalContext(updatedContext + `\nassistant: ${cleanedGptResponse}`);

        // Move to next step
        setConversationalStep(nextStepIndex);
      } else {
        // End of conversation flow
        console.log('Conversational flow completed');
        setIsInConversationalFlow(false);
        setConversationalStep(0);
      }

      // Update conversation
      await updateDoc(doc(db, `conversations/${conversationId}`), {
        lastMessage: userResponse.substring(0, 100),
        updatedAt: new Date(),
      });

    } catch (error) {
      console.error('Error handling conversational response:', error);
      setError(t('errorProcessingResponse'));
    }
  };



  // Function to switch to Plan Q&A tab
  const switchToPlanQa = (context: PlanQaContext) => {
    // Check if user is authenticated before switching to Plan Q&A
    if (!user) {
      // Show error message or redirect to sign in
      alert(t('auth.signInError'));
      return;
    }

    setActiveTab('planQa');
    setPlanContext(context);
    setHasPlanQaMessages(true); // Mark that we now have Plan Q&A messages
    if (onSwitchToPlanQa) {
      onSwitchToPlanQa(context);
    }
  };

  // Handle Plan Q&A message sending
  const handlePlanQaMessage = (message: string) => {
    // This will be handled by the PlanQaTab component
    console.log('Plan Q&A message sent:', message);
  };



  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralChatContent();
      case 'planQa':
        return (
          <PlanQaTab
            planContext={planContext}
            onSendMessage={handlePlanQaMessage}
            conversationId={conversationId}
            user={user}
          />
        );
      case 'memory':
        return (
          <RecentConversationMemory
            userId={user?.uid || ''}
            petName={petName}
            petType={petType}
            language={memoryLanguage}
            onMemoryContextUpdate={() => { }}
          />
        );
      default:
        return renderGeneralChatContent();
    }
  };

  // Extract current chat content for tabbed view
  const renderGeneralChatContent = () => {
    return (
      <>
        {/* Messages Area */}
        <ChatMessageList
          messages={messages}
          user={user}
          isTyping={isTyping}
          loadingProgress={loadingProgress}
          loadingTime={loadingTime}
          isGeneratingRoadmap={isGeneratingRoadmap}
          showScrollToBottom={showScrollToBottom}
          onScrollToBottom={() => {
            const container = document.querySelector('[data-messages-container]');
            scrollToBottom(container);
          }}
          editingMessage={editingMessage}
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          onConfirmEdit={handleEditMessage}
          setEditingMessage={setEditingMessage}
          onRoadmapTrigger={handleRoadmapTrigger}
        />

        {/* Input Area */}
        <Box sx={{ p: 2, bgcolor: 'white', borderTop: 1, borderColor: 'divider' }}>
          <ChatInputBar
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            isLoading={isLoading}
            threadId={threadId}
            isInitializingThread={isInitializingThread}
            isMobile={isMobile}
            onSendMessage={handleSendMessage}
          />
        </Box>
      </>
    );
  };





  // Simple language detection and translation for UI elements
  const getUIText = (key: string) => {
    // Detect language from recent messages
    const recentMessages = messages.slice(-5);
    const recentText = recentMessages.map(m => m.content).join(' ');

    const isChinese = /[\u4e00-\u9fff]/.test(recentText);
    const isSpanish = /[áéíóúñü]/.test(recentText.toLowerCase());
    const isFrench = /[àâäéèêëïîôöùûüÿç]/.test(recentText.toLowerCase());

    if (isChinese) {
      const chineseTexts: Record<string, string> = {
        backToChat: '返回聊天'
      };
      return chineseTexts[key] || key;
    } else if (isSpanish) {
      const spanishTexts: Record<string, string> = {
        backToChat: 'Volver al Chat'
      };
      return spanishTexts[key] || key;
    } else if (isFrench) {
      const frenchTexts: Record<string, string> = {
        backToChat: 'Retour au Chat'
      };
      return frenchTexts[key] || key;
    } else {
      // English
      const englishTexts: Record<string, string> = {
        backToChat: 'Back to Chat'
      };
      return englishTexts[key] || key;
    }
  };

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'grey.50'
    }}>
      {/* Chat Header */}
      <Box sx={{
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <Avatar sx={{ bgcolor: 'transparent', width: 40, height: 40 }}>
          <img
            src="/logo192.png"
            alt="PetSnap Assistant"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6">
            {t('chatHeader')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('chatSubheader')}
          </Typography>
        </Box>
        {showRoadmapView && (
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleReturnToChat}
            size="small"
          >
            {getUIText('backToChat')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Main Content Area - Chat with Tabs or Roadmap View */}
      <Box sx={{
        flexGrow: 1,
        display: 'flex',
        overflow: 'hidden',
        flexDirection: isMobile && showRoadmapView ? 'column' : 'row'
      }}>
        {/* Chat Section with Tabs - Left side when roadmap is open */}
        <Box sx={{
          flexGrow: showRoadmapView ? 0 : 1,
          width: isMobile ? '100%' : (showRoadmapView ? '40%' : '100%'),
          display: 'flex',
          flexDirection: 'column',
          borderRight: showRoadmapView && !isMobile ? 1 : 0,
          borderBottom: isMobile && showRoadmapView ? 1 : 0,
          borderColor: 'divider',
          transition: 'all 0.3s ease-in-out'
        }}>

          {/* Tab Navigation - Only show when there are multiple tabs */}
          <ChatTabBar
            activeTab={activeTab}
            onTabChange={setActiveTab}
            hasRoadmap={!!currentRoadmap}
            hasPlanQaMessages={hasPlanQaMessages}
            hasPetMemory={!!petName}
          />

          {/* Tab Content */}
          <Box sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {renderTabContent()}
          </Box>
        </Box>

        {/* Roadmap Section - Right side when roadmap is open */}
        {showRoadmapView && currentRoadmap && (
          <Box sx={{
            flexGrow: 1,
            width: isMobile ? '100%' : '60%',
            height: isMobile ? '50vh' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'white',
            transition: 'all 0.3s ease-in-out'
          }}>
            {currentRoadmap.category === 'dynamic' ? (
              <FlexibleRoadmap
                key={`roadmap-${currentRoadmap.id}`}
                open={true}
                onClose={() => onRoadmapViewToggle(null)}
                roadmap={currentRoadmap}
                userId={user?.uid || ''}
                userLanguage={currentRoadmap.userLanguage}
                isArtifactView={true}
                onAskQuestion={switchToPlanQa}
                onRoadmapCompleted={handleRoadmapCompleted}
                showStickerStats={showStickerStats}
                onStickerStatsOpen={handleOpenStickerStats}
                onStickerStatsClose={handleCloseStickerStats}
              />
            ) : (
              <RoadmapModule
                open={true}
                onClose={() => onRoadmapViewToggle(null)}
                userId={user?.uid || ''}
                planId={currentRoadmap.id}
                planTitle={currentRoadmap.title}
                planDescription={currentRoadmap.description}
                isArtifactView={true}
                onAskQuestion={switchToPlanQa}
              />
            )}
          </Box>
        )}
      </Box>



      <style>
        {`
          @keyframes pulse {
            0%, 60%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            30% {
              transform: scale(1.1);
              opacity: 0.7;
            }
          }
        `}
      </style>

      {/* Sticker Statistics Dialog */}
      <StickerStats
        userId={user?.uid || ''}
        open={showStickerStats}
        onClose={handleCloseStickerStats}
        userLanguage={stickerStatsLanguage}
      />
    </Box>
  );
};

export default Chat;